import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Loader from '../components/common/Loader';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const OpportunityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, role, isStudent: isStudentUser } = useAuth();
  const { addToast } = useToast();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  
  const isStudent = isStudentUser;

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await apiClient.get(`/opportunities/${id}`);
        setJob(response.data);
        
        // If student is logged in, check if they applied
        if (isStudent && isAuthenticated) {
          try {
            const appRes = await apiClient.get('/student/applications');
            const applications = appRes.data;
            const applied = applications.some(app => app.opportunity_id === parseInt(id));
            setHasApplied(applied);
          } catch (e) {
            console.error('Failed to fetch applications', e);
          }
        }
      } catch (err) {
        addToast('Failed to load opportunity details', 'error');
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };
    
    fetchJob();
  }, [id, isStudent, isAuthenticated, navigate, addToast]);

  const handleApply = async () => {
    if (!isAuthenticated) {
      addToast('Please login to apply', 'info');
      navigate('/login');
      return;
    }
    
    if (!isStudent) {
      addToast('Only students can apply to jobs', 'error');
      return;
    }
    
    setApplying(true);
    try {
      await apiClient.post(`/opportunities/${id}/apply`);
      addToast('Application submitted successfully!', 'success');
      setHasApplied(true);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to apply. You might have missing profile fields.', 'error');
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <Loader fullPage />;
  if (!job) return null;

  return (
    <div className="page-wrapper">
      <Navbar />
      
      <div className="bg-secondary text-white pt-12 pb-24">
        <div className="container">
          <div className="page-back-bar !bg-transparent !border-none !p-0 mb-6">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white flex items-center gap-2 text-sm">
              ← Back to listings
            </button>
          </div>
          
          <div className="flex justify-between items-start flex-wrap gap-6">
            <div>
              <h1 className="heading-1 text-white mb-2">{job.title}</h1>
              <div className="text-lg text-gray-300 mb-6">{job.company_name}</div>
              
              <div className="flex flex-wrap gap-4 mt-4">
                <Badge variant="primary" className="!bg-primary/20 !text-primary-light border border-primary/30 py-1.5 px-3">
                  {job.employment_type || 'Full Time'}
                </Badge>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <span>📍</span> {job.location || 'Remote'}
                </div>
                {job.salary && (
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <span>💰</span> {job.salary}
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <span>🕒</span> Apply by {new Date(job.deadline).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="flex-shrink-0 w-full sm:w-auto">
              <div className="card-dark p-6 min-w-[280px]">
                <h3 className="text-lg font-semi mb-4">Ready to apply?</h3>
                {hasApplied ? (
                  <Button variant="success" className="w-full" disabled>
                    ✓ Applied
                  </Button>
                ) : (
                  <Button 
                    variant="primary" 
                    className="w-full" 
                    size="lg"
                    onClick={handleApply}
                    isLoading={applying}
                  >
                    Apply Now
                  </Button>
                )}
                {!isAuthenticated && (
                  <p className="text-xs text-gray-400 text-center mt-3">You will be asked to log in</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <main className="main-content bg-gray-50 -mt-12 pb-20">
        <div className="container">
          <div className="card-elevated">
            <div className="grid grid-3 gap-12">
              <div className="col-span-2 flex-col gap-10">
                <section>
                  <h3 className="heading-3 mb-4">Job Description</h3>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {job.description}
                  </div>
                </section>
                
                {job.eligibility && (
                  <section>
                    <h3 className="heading-3 mb-4">Eligibility Criteria</h3>
                    <div className="bg-primary-xlight border border-primary-light rounded-lg p-6 text-gray-800">
                      {job.eligibility}
                    </div>
                  </section>
                )}
              </div>
              
              <div className="col-span-1">
                <div className="sticky top-24">
                  <h3 className="heading-3 mb-4">Required Skills</h3>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {job.skills ? job.skills.split(',').map((skill, index) => (
                      <Badge key={index} variant="gray" className="py-1.5 px-3 text-sm">
                        {skill.trim()}
                      </Badge>
                    )) : <p className="text-sm text-gray-500">Not specified</p>}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-8">
                    <h3 className="heading-3 mb-4">About {job.company_name}</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Top-tier company hiring through Million Minds platform.
                    </p>
                    <Button variant="outline" className="w-full">View Company Profile</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default OpportunityDetail;
